import { createContext, useContext, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { companiesApi } from "@/api/companies";
import { tenantUsersApi, type TenantUser } from "@/api/tenant-users";
import { queryKeys } from "@/lib/query-keys";

interface TenantUserMe extends TenantUser {
  isInstanceAdmin: boolean;
}

/**
 * Three-tier tenant role model (server migration 0056):
 *   tenant_admin   — sees and manages every agent, can grant access,
 *                    manages users/groups/integrations.
 *   agent_admin    — manages only agents granted via agent_access;
 *                    can manage tenant skill + MCP catalogues.
 *   standard_user  — chat-only, no settings cog anywhere.
 *
 * Legacy role strings (admin/editor/viewer) are still accepted from
 * older sessions and mapped to the canonical names below.
 */
export type TenantRole = "tenant_admin" | "agent_admin" | "standard_user";

function canonRole(raw: string | undefined): TenantRole {
  switch (raw) {
    case "admin":
    case "tenant_admin":
      return "tenant_admin";
    case "editor":
    case "agent_admin":
      return "agent_admin";
    default:
      return "standard_user";
  }
}

interface TenantContextValue {
  tenantId: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  isLoading: boolean;
  /** Canonical role within this tenant. */
  userRole: TenantRole;
  /** Whether current user is an MSP instance admin. */
  isInstanceAdmin: boolean;
  /** True for tenant_admin or instance admin — full tenant power. */
  isTenantAdmin: boolean;
  /**
   * True for agent_admin (only). Use together with `useAgentAccess(agentId)`
   * for per-agent management gating; this flag alone just tells you the
   * user *could* manage some agents.
   */
  isAgentAdmin: boolean;
  /** Standard user — chat-only, no settings cog. */
  isStandardUser: boolean;
  /**
   * Legacy alias for `isTenantAdmin`. Many UI files were written against
   * the old two-tier model; keeping this alias avoids a sweeping rename
   * in the same change as the role model split.
   */
  isAdmin: boolean;
  /**
   * True if the user can manage *some* agents in the tenant (tenant_admin
   * or agent_admin). Per-agent gating still needs `useAgentAccess`.
   */
  canManageAnyAgent: boolean;
  /**
   * Legacy: was "editor or above". Kept so existing call sites compile;
   * use `canManageAnyAgent` for new code and `useAgentAccess(agentId)`
   * for per-agent decisions.
   */
  canEditAgents: boolean;
  /** Current user's tenant_user ID (null for instance admins without tenant record) */
  tenantUserId: string | null;
  /** Whether the tenant has completed onboarding */
  onboardingComplete: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { tenantId } = useParams<{ tenantId: string }>();

  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: queryKeys.companies.detail(tenantId!),
    queryFn: () => companiesApi.get(tenantId!),
    enabled: !!tenantId,
  });

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: queryKeys.tenantUserMe.byCompany(tenantId!),
    queryFn: () =>
      fetch(`/api/companies/${tenantId}/users/me`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null) as Promise<TenantUserMe | null>,
    enabled: !!tenantId,
  });

  const isInstanceAdmin = me?.isInstanceAdmin ?? false;
  const userRole: TenantRole = canonRole(me?.role);
  const isTenantAdmin = isInstanceAdmin || userRole === "tenant_admin";
  const isAgentAdmin = !isTenantAdmin && userRole === "agent_admin";
  const isStandardUser = !isTenantAdmin && !isAgentAdmin;
  const canManageAnyAgent = isTenantAdmin || isAgentAdmin;
  // Legacy aliases — kept so call sites that pre-date the role split
  // keep compiling. New code should prefer the canonical flags above.
  const isAdmin = isTenantAdmin;
  const canEditAgents = canManageAnyAgent;
  const onboardingComplete = company?.onboardingComplete ?? true;

  const navigate = useNavigate();
  const location = useLocation();

  // Redirect tenant admins (non-instance-admins) to onboarding if not complete
  useEffect(() => {
    if (companyLoading || meLoading) return;
    if (!tenantId) return;
    if (isInstanceAdmin) return; // MSP admins skip onboarding
    if (onboardingComplete) return;
    if (!isTenantAdmin) return; // only tenant admins can complete onboarding
    if (location.pathname.includes("/onboarding")) return;
    navigate(`/tenants/${tenantId}/onboarding`, { replace: true });
  }, [companyLoading, meLoading, tenantId, isInstanceAdmin, onboardingComplete, isTenantAdmin, location.pathname, navigate]);

  return (
    <TenantContext.Provider
      value={{
        tenantId: tenantId!,
        tenantName: company?.name ?? "Loading...",
        tenantLogoUrl: company?.logoUrl ?? null,
        isLoading: companyLoading || meLoading,
        userRole,
        isInstanceAdmin,
        isTenantAdmin,
        isAgentAdmin,
        isStandardUser,
        canManageAnyAgent,
        isAdmin,
        canEditAgents,
        tenantUserId: me?.id ?? null,
        onboardingComplete,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within a TenantProvider");
  return ctx;
}

export function useOptionalTenant(): TenantContextValue | null {
  return useContext(TenantContext);
}
